<script setup lang="ts">
import useImagePath from '~/composables/useImagePath';
import LazyImage from '~/components/design-system/lazy-image.vue';
import useLaunchGame from '~/composables/useLaunchGame';
import { trackingMethods } from '~/plugins/analytics.client';
import { useAuthStore } from '~/stores/auth.store';
import { chunk } from '~/utilities/data-transformers';
import CategoryCardLaunchButtons from './category-card-launch-buttons.vue';
import GenericTag from '../user-interface/generic-tag.vue';
import ExternalIcon from '../design-system/icons/ExternalIcon.vue';
import type { IGameCategory } from '~/interfaces/dto/games/response';

const { $enabled } = useNuxtApp();

const launchGameComp = useLaunchGame;
const auth = useAuthStore();
const gameStore = useGameStore();

const { getPlaceholder } = useBranding();

interface IMasonryGridProps {
  title: string;
  perPage?: number | null;
  games: Array<IGameCategory>;
  route?: string;
  displayShowAll?: boolean;
  alias?: string;
}

const useImage = useImagePath;

const props = withDefaults(defineProps<IMasonryGridProps>(), {
  title: '',
  perPage: 6,
  route: '',
  displayShowAll: true,
  alias: '',
});

const ones = [
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];

const gameUnavailable = computed(() => {
  return (game: IGameCategory) => {
    if (game?.unavailable) return true;
    return gameStore.returnDisabledProviders.includes(game?.provider);
  };
});
function launchGame(game: any, freePlay = false) {
  if (gameUnavailable.value(game)) return;
  launchGameComp({ game, freePlay, component: 'masonry-grid' });

  // send GA tracking method when game is launched
  trackingMethods.launchReal(
    game.name,
    auth?.currentUser?.username,
    'category-masonry-simple.vue',
  );
}

const computedLayout = computed(() => {
  const categoryExpand = [...props.games];
  const chunks = chunk(categoryExpand, 4);
  const modifiedChunks = chunks.map((chunk) => {
    return chunk.map((c, index) => {
      let bindings = {};
      let tileImage, placeholderUrl;
      switch (ones[index]) {
        case 'one':
          tileImage = c?.image?.horizontalTile;
          placeholderUrl = getPlaceholder('masonryPlaceholderOne');
          break;
        case 'two':
          tileImage = c?.image?.verticalTile;
          placeholderUrl = getPlaceholder('masonryPlaceholderTwo');
          break;
        default:
          tileImage = c?.image?.squareTile;
          placeholderUrl = getPlaceholder('masonryPlaceholderOne');
      }
      Object.assign(bindings, {
        colspan: 2,
        class: [
          ones[index],
          'rounded-lg',
          'overflow-hidden',
          'glow',
          'relative',
        ],
      });
      return { ...c, bindings, tileImage, placeholderUrl };
    });
  });
  return modifiedChunks.splice(0, 1);
});
</script>

<template>
  <div class="w-full my-2">
    <div
      class="pb-2 pt-7 pl-1 text-base-priority font-bold truncate capitalize flex items-center text-xl"
    >
      <slot name="icon" />
      <ExternalIcon v-if="!$slots.icon" :icon-name="alias" class="!mx-2" />
      {{ props.title }}
      <slot name="subtitle" />
    </div>
    <div
      v-for="(chunk, index) in computedLayout"
      :key="index"
      class="category-masonry"
    >
      <div
        v-for="(game, ii) in chunk"
        :key="`${index}-${ii}`"
        class="game-card relative"
        v-bind="game.bindings"
        @click="
          auth.loggedIn || !game.freeplay || !$enabled('website.enablefreeplay')
            ? launchGame(game, false)
            : null
        "
      >
        <CategoryCardMaintenance v-if="gameUnavailable(game)" />
        <CategoryCardLaunchButtons
          v-if="
            !auth.loggedIn &&
            !gameUnavailable(game) &&
            $enabled('website.enablefreeplay')
          "
          class="launch-buttons hidden transition-all duration-100 ease-in-out"
          :has-freeplay="game.freeplay"
          @free-play="launchGame(game, true)"
          @real-play="launchGame(game, false)"
        />
        <LazyImage
          class="masonry-image"
          :src="useImage(game.tileImage)"
          :placeholder="game.placeholderUrl"
          :alt="game.name"
        />
        <GenericTag
          v-if="game?.exclusive"
          type="exclusive"
          position="top-left"
        />
        <GenericTag v-if="game?.isNew" type="new" position="bottom-left" />
        <GenericTag
          v-if="game?.isPromoted"
          type="promo"
          position="bottom-left"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.game-card:hover > .launch-buttons {
  display: block;
}
</style>
